import { AlertTriangle, Check, Info } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EmptyState, LoadingRows } from '@/components/ui/async-state';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { newestSeen, useMarkAllRead, useMarkRead } from '../api/notifications-api';
import { overflowHref } from '../lib/bell-href';
import type { BellConnection, NeedsActionGroup, NotificationsSnapshot, StaffUpdate } from '../types';
import { NeedsActionRow, UpdateRow } from './bell-item';

type Tab = 'needs' | 'updates';

/**
 * The bell's content, shared by the desktop popover and the mobile bottom
 * sheet (spec R2–R5, R7 footer): the header with Mark all read, the two tabs,
 * and the footer saying whether the list is live.
 *
 * Loading, could-not-load, empty and partial ("some items couldn't be
 * checked") are four different things on screen — a failed read is never
 * drawn as "all caught up" (AC6.3).
 */
export function BellPanel({
  snapshot,
  isPending,
  isError,
  onRetry,
  connection,
  onNavigate,
  titleId,
  inSheet = false,
}: {
  snapshot: NotificationsSnapshot | undefined;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  connection: BellConnection;
  /** A row's action was followed: close the bell. */
  onNavigate: () => void;
  /** Labels the popover; the sheet carries its own title. */
  titleId: string;
  inSheet?: boolean;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('needs');
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const updates = snapshot?.updates.items;
  const pushedIn = usePushedIn(updates);
  const upTo = updates ? newestSeen(updates) : null;
  const unread = snapshot?.updates.unreadCount ?? 0;
  const needsCount = snapshot?.needsAction.count ?? 0;

  const openUpdate = (update: StaffUpdate): void => {
    if (update.readAt === null) markRead.mutate(update.id);
  };

  const body = (content: () => ReactNode) => {
    if (isError && !snapshot) {
      return (
        <div className="px-6 py-7 text-center">
          <p className="text-sm font-semibold">{t('notifications.error.title')}</p>
          <p className="mb-3 mt-1 text-[12.5px] text-muted-foreground">{t('notifications.error.body')}</p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t('notifications.error.retry')}
          </Button>
        </div>
      );
    }
    if (isPending || !snapshot) {
      return <LoadingRows rows={3} className="px-4 py-3.5" label={t('notifications.loading')} />;
    }
    return content();
  };

  const list = cn(!inSheet && 'max-h-[420px] overflow-y-auto');

  return (
    <div className={cn('flex flex-col', inSheet && '-mx-6 -my-5')}>
      <div className={cn('flex items-center px-4', inSheet ? 'justify-end pt-2' : 'justify-between pt-3.5')}>
        {inSheet ? null : (
          <h2 id={titleId} className="text-[15px] font-bold">
            {t('notifications.title')}
          </h2>
        )}
        <button
          type="button"
          disabled={tab !== 'updates' || unread === 0 || upTo === null || markAllRead.isPending}
          onClick={() => upTo && markAllRead.mutate(upTo)}
          className="min-h-6 rounded-sm text-[12.5px] font-semibold text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:text-muted-foreground/60 disabled:no-underline"
        >
          {t('notifications.markAllRead')}
        </button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="mt-2">
        <TabsList aria-label={t('notifications.tabs.label')}>
          <TabsTrigger value="needs" count={needsCount > 0 ? needsCount : undefined} urgent>
            {t('notifications.tabs.needsAction')}
          </TabsTrigger>
          <TabsTrigger value="updates" count={unread > 0 ? t('notifications.tabs.unread', { count: unread }) : undefined}>
            {t('notifications.tabs.updates')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="needs">
          {body(() => (
            <>
              {snapshot!.degraded.length > 0 ? (
                <p className="mx-4 mt-2.5 flex items-start gap-2 rounded-lg bg-status-amber-tint px-2.5 py-2 text-xs text-[#9A3412]">
                  <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
                  {t('notifications.degraded')}
                </p>
              ) : null}
              {snapshot!.needsAction.groups.length === 0 ? (
                // Never "all caught up" while a check failed (AC6.3).
                snapshot!.degraded.length > 0 ? null : (
                  <EmptyState
                    icon={
                      <span className="grid size-11 place-items-center rounded-full bg-status-green-tint text-status-green">
                        <Check className="size-5" aria-hidden />
                      </span>
                    }
                    title={t('notifications.empty.caughtUpTitle')}
                    description={t('notifications.empty.caughtUpBody')}
                  />
                )
              ) : (
                <div className={list}>
                  {snapshot!.needsAction.groups.map((group) => (
                    <NeedsActionGroupList key={group.type} group={group} onNavigate={onNavigate} />
                  ))}
                </div>
              )}
            </>
          ))}
        </TabsContent>

        <TabsContent value="updates">
          {body(() =>
            snapshot!.updates.items.length === 0 ? (
              <EmptyState
                icon={
                  <span className="grid size-11 place-items-center rounded-full bg-status-blue-tint text-primary">
                    <Info className="size-5" aria-hidden />
                  </span>
                }
                title={t('notifications.empty.noUpdatesTitle')}
                description={t('notifications.empty.noUpdatesBody')}
              />
            ) : (
              <ul className={list}>
                {snapshot!.updates.items.map((update) => (
                  <UpdateRow
                    key={update.id}
                    update={update}
                    highlight={pushedIn.has(update.id)}
                    now={Date.now()}
                    onOpen={openUpdate}
                    onNavigate={onNavigate}
                  />
                ))}
              </ul>
            ),
          )}
        </TabsContent>
      </Tabs>

      <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 border-t px-4 py-2.5 text-[11.5px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5" role="status">
          <span
            className={cn('size-1.5 rounded-full', connection === 'live' ? 'bg-status-green' : 'bg-status-amber')}
            aria-hidden
          />
          {connection === 'live' ? t('notifications.footer.live') : t('notifications.footer.reconnecting')}
        </span>
        <span>{tab === 'updates' ? t('notifications.footer.latest') : t('notifications.footer.fixedLeave')}</span>
      </div>
    </div>
  );
}

/** One type's rows under "Label · count", with the overflow link past the cap (AC3.0, AC3a.3). */
function NeedsActionGroupList({ group, onNavigate }: { group: NeedsActionGroup; onNavigate: () => void }) {
  const { t } = useTranslation();
  const label = t(`notifications.group.${group.type}`);
  // The invitations item is one row summarising N invitations: its "count" of 1
  // would misread as one invitation, so the Team heading carries no number.
  const heading = group.type === 'invitations.judges' ? label : t('notifications.groupHeading', { label, count: group.count });
  // The true total, not the server's page: rows beyond the cap (AC3.1).
  const hidden = group.count - group.items.length;
  return (
    <section>
      <h3 className="px-4 pb-1 pt-3 text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{heading}</h3>
      <ul>
        {group.items.map((item) => (
          <NeedsActionRow key={item.id} item={item} onNavigate={onNavigate} />
        ))}
      </ul>
      {group.overflow && hidden > 0 ? (
        <div className="border-t px-4 py-2.5">
          <Link
            to={overflowHref(group.overflow)}
            onClick={onNavigate}
            className="rounded-sm text-[12.5px] font-semibold text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('notifications.overflow', { count: hidden })}
          </Link>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Updates that arrived while the bell was open — the ones a push brought in.
 * The first list drawn is the baseline; anything newer is "Just now".
 */
function usePushedIn(items: readonly StaffUpdate[] | undefined): ReadonlySet<string> {
  const [baseline, setBaseline] = useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (items && baseline === null) setBaseline(new Set(items.map((u) => u.id)));
  }, [items, baseline]);
  if (!items || baseline === null) return EMPTY;
  return new Set(items.filter((u) => !baseline.has(u.id)).map((u) => u.id));
}

const EMPTY: ReadonlySet<string> = new Set();
